import type { TFunction } from 'i18next';
import type { Cart, HardwareFirstRecurringCharge } from '../types/carts';
import type { IVariant } from '../types';
import {
  HardwareFirstMembershipTerm,
  IProduct,
  miniCollarId,
  PricingByCurrency,
  ProductCategory,
  series3PlusCollarId,
  SizeOption,
  ultraCollarId,
} from '../types/Product';

/** mirrors web's HARDWARE_FIRST_MAX_UNITS_PER_CART - the server rejects carts above it */
export const HARDWARE_FIRST_MAX_UNITS_PER_CART = 5;

/** the earliest the first membership charge can land: the order, set up at once, plus 30 + 7 days */
export const HARDWARE_FIRST_TRIAL_MEMBERSHIP_DAYS = 37;

/**
 * The membership a hardware-first trial bills once the trial ends: the 12-month term at its intro price for
 * the first year, then its list price. The real start follows the shopper's setup date, unknown at checkout,
 * so the schedule starts at the earliest possible date. Undefined until the terms load or when none is 12 months.
 */
export function hardwareFirstTrialRecurringCharge(
  terms: HardwareFirstMembershipTerm[] | undefined,
  priceInCents: (pricing: PricingByCurrency) => number,
  orderedAt: Date,
): HardwareFirstRecurringCharge | undefined {
  const term = terms?.find((candidate) => candidate.renewalMonths === 12);
  if (!term) {
    return undefined;
  }
  const startsAt = new Date(orderedAt);
  startsAt.setUTCDate(startsAt.getUTCDate() + HARDWARE_FIRST_TRIAL_MEMBERSHIP_DAYS);
  return {
    amountInCents: priceInCents(term.introPriceByCurrency),
    interval: 'month',
    intervalCount: term.renewalMonths,
    quantity: 1,
    recurringPaymentStartDate: startsAt.toISOString(),
    renewalAmountInCents: priceInCents(term.listPriceByCurrency),
  };
}

/** One- or two-letter size labels for the solo page's chips and its sizing sheet's badge. */
export const HARDWARE_FIRST_SIZE_SHORT_KEYS: Record<SizeOption, string> = {
  [SizeOption.XSmall]: 'size_xs',
  [SizeOption.Small]: 'size_s',
  [SizeOption.Medium]: 'size_m',
  [SizeOption.Large]: 'size_l',
  [SizeOption.XLarge]: 'size_xl',
};

/** The size names the hardware-first surfaces use: "Extra Small" and "Extra Large" written out, the rest as given. */
export function hardwareFirstSizeName(t: TFunction, size: SizeOption, fallback: string): string {
  if (size === SizeOption.XSmall) return t('hardware_first_size_name_x_small');
  if (size === SizeOption.XLarge) return t('hardware_first_size_name_x_large');
  return fallback;
}

interface PriceFormatters {
  currencyFormatter: Intl.NumberFormat;
  wholeNumberCurrencyFormatter: Intl.NumberFormat;
}

/**
 * Money on every hardware-first surface: a whole-dollar amount shows without cents ($99, $5),
 * anything else shows them ($69.30).
 */
export function formatHardwarePrice(cents: number, formatters: PriceFormatters): string {
  const formatter = cents % 100 === 0 ? formatters.wholeNumberCurrencyFormatter : formatters.currencyFormatter;
  return formatter.format(cents / 100);
}

/**
 * Total hardware-first units in the cart: standalone (subscription-less) collar items. Only
 * meaningful for enrolled visitors - callers gate on enrollment; the server remains the
 * authority on which collars are actually repriced.
 *
 * Kept out of `lib/cart.ts` for the same reason as `lib/freeShipping.ts`: that module reaches
 * `lib/product` → `lib/util` → `uuid`, which Jest cannot transform; as a leaf this stays
 * unit-testable without mocking that chain.
 */
export function hardwareFirstUnitCount(cart: Cart, products: IProduct[]): number {
  const collarSkus = new Set(
    products.flatMap((product) => {
      if (product.category === ProductCategory.COLLAR_KIT) {
        return product.variants.map((variant) => variant.sku);
      }
      if (product.category === ProductCategory.MINI || product.category === ProductCategory.ULTRA) {
        return [product.sku];
      }
      return [];
    }),
  );

  return Object.values(cart.cartItems).reduce((count, cartItem) => {
    const isStandaloneItem = !('subscriptionLineItem' in cartItem) && !('giftCardDelivery' in cartItem);
    return isStandaloneItem && collarSkus.has(cartItem.lineItem.sku) ? count + cartItem.quantity : count;
  }, 0);
}

export interface FormattedMembershipTerm {
  renewalMonths: number;
  /** the first term after the intro discount */
  intro: string;
  /** what the term renews at */
  renewal: string;
  /** the intro spread over the term, rounded up to the currency's smallest whole price */
  monthly: string;
  monthlyInCents: number;
}

export interface MembershipTermPricing {
  /** resolves a per-currency table to the active currency, in minor units */
  priceInCents: (pricing: PricingByCurrency) => number;
  /** the smallest amount a price can end in: 100 for isk, which is stored x100 but has no minor unit */
  priceStepInCents: number;
}

/**
 * The membership terms `/products` publishes, priced in the active currency and formatted for
 * display. The monthly figure rounds up so "as low as" never quotes less than the customer pays.
 */
export function formatMembershipTerms(
  terms: HardwareFirstMembershipTerm[] | undefined,
  { priceInCents, priceStepInCents }: MembershipTermPricing,
  formatters: PriceFormatters,
): FormattedMembershipTerm[] {
  return (terms ?? []).map((term) => {
    const introInCents = priceInCents(term.introPriceByCurrency);
    const monthlyInCents = Math.ceil(introInCents / term.renewalMonths / priceStepInCents) * priceStepInCents;
    return {
      renewalMonths: term.renewalMonths,
      intro: formatHardwarePrice(introInCents, formatters),
      renewal: formatHardwarePrice(priceInCents(term.listPriceByCurrency), formatters),
      monthly: formatHardwarePrice(monthlyInCents, formatters),
      monthlyInCents,
    };
  });
}

/** the cheapest monthly figure across the terms, for "as low as" copy; undefined until the terms load */
export function lowestMonthlyPrice(terms: FormattedMembershipTerm[]): string | undefined {
  return terms.reduce<FormattedMembershipTerm | undefined>(
    (lowest, term) => (lowest && lowest.monthlyInCents <= term.monthlyInCents ? lowest : term),
    undefined,
  )?.monthly;
}

/** The redesigned cart's product shots: the mini in a hand, the series 3+ band close-up, the ultra alone. */
const HARDWARE_FIRST_CART_IMAGES: Record<string, string> = {
  [miniCollarId]: '/product_images/cart/mini-hand.png',
  [series3PlusCollarId]: '/product_images/cart/s3-band.png',
  [ultraCollarId]: '/product_images/cart/ultra.png',
};

/**
 * The image a redesigned cart or checkout row shows for a tracker. A line with a band variant keeps
 * its own collar colour, which `fallback` carries; anything else takes the cart shot, or the
 * catalog image when there is none.
 */
export function hardwareFirstCartImageUrl(productId: string, variant: IVariant | undefined, fallback: string): string {
  return variant ? fallback : (HARDWARE_FIRST_CART_IMAGES[productId] ?? fallback);
}
